import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { atxIsMobile$ } from '@epicuro-next/ataraxia/breakpoint';
import {
  ProductListConfigurationKeys,
  PRODUCT_LIST_NAMESPACE,
} from '@epicuro-next/constants/rule-engine/product-list-rules';
import { List, LookBook } from '@epicuro-next/models/engagement';
import { plural } from '@epicuro-next/platform/i18n';
import { selectShowLookBookNameInTitle } from '@epicuro-next/state/product-list-state';
import { selectLookBookCommercialListInRoute } from '@epicuro-next/state/shop-the-look-state';
import { isNonNullish, isNullish } from '@epicuro-next/utilities';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@Component({
  selector: 'epc-list-header',
  templateUrl: './list-header.component.html',
  styleUrls: ['./list-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ListHeaderComponent {
  @Input()
  public list?: List;

  @Input()
  public lookBook?: LookBook;

  public isMobile$: Observable<boolean> = atxIsMobile$;

  public showLookBookTitle$ = this.store.pipe(
    select(selectShowLookBookNameInTitle),
    filter(isNonNullish),
  );

  public showDescription$ = this.store.pipe(
    select(selectLookBookCommercialListInRoute),
    map((isCommercialListInLookBook) => !isCommercialListInLookBook),
  );

  public productListNamespace = PRODUCT_LIST_NAMESPACE;

  public showDescriptionEditor =
    ProductListConfigurationKeys.ShowDescriptionEditor;

  constructor(private store: Store) {}

  public get dateUtc() {
    const update = this.list?.update?.dateUtc ?? this.lookBook?.updatedAt;
    const create = this.list?.creation?.dateUtc ?? this.lookBook?.createdAt;
    return update ?? create;
  }

  public get user() {
    return this.list?.creation?.user ?? this.lookBook?.creation.user;
  }

  public get header() {
    if (this.list) return this.list.name;
    if (this.lookBook) return this.lookBook.title;
    return undefined;
  }

  public get shareableList() {
    return this.list ?? this.lookBook?.items[0].productList;
  }

  /**
   * Returns empty string to avoid jumping layout
   */
  public get productCountText(): string {
    if (!this.list) return '';

    const { availableProductsCount } = this.list;

    if (isNullish(availableProductsCount)) return '';

    return this.getAvailableProductCountText(availableProductsCount);
  }

  private getAvailableProductCountText(count: number) {
    return plural(
      count,
      {
        1: $localize`:list header|single available product count@@listHeaderSingleAvailableProductCount:${count} available product`,
      },
      $localize`:list header|available product count@@listHeaderAvailableProductCount:${count} available products`,
    );
  }
}
